import { pick } from 'lodash'

import {
  ELocationKeys,
  TBulkAssignAgentEntry,
  TCreateLocationPayload,
  TUpdateLocationPayload,
} from '~/features/agents/models'
import { ELocationFormKeys, TLocationForm } from '~/features/locations/models'
import {
  getDefaultLocationFormValues,
  normalizeEmptyStringsToNull,
} from '~/features/locations/utils'
import getChangedFields from '~/utils/form'

const LOCATION_PAYLOAD_FIELDS = [
  ELocationFormKeys.name,
  ELocationFormKeys.streetAddress1,
  ELocationFormKeys.streetAddress2,
  ELocationFormKeys.city,
  ELocationFormKeys.state,
  ELocationFormKeys.zipCode,
  ELocationFormKeys.timezone,
  ELocationFormKeys.hoursText,
  ELocationFormKeys.type,
] as const

const CREATE_LOCATION_OPTIONAL_FIELDS = [
  ELocationFormKeys.streetAddress1,
  ELocationFormKeys.streetAddress2,
  ELocationFormKeys.city,
  ELocationFormKeys.state,
  ELocationFormKeys.zipCode,
  ELocationFormKeys.timezone,
  ELocationFormKeys.hoursText,
] as const

export const getEditLocationPayload = ({
  initialValues,
  locationId,
  values,
}: {
  initialValues: TLocationForm
  locationId: string
  values: TLocationForm
}): TUpdateLocationPayload => {
  const currentLocationValues = pick(values, LOCATION_PAYLOAD_FIELDS)
  const initialLocationValues = pick(initialValues, LOCATION_PAYLOAD_FIELDS)

  const changedFields = getChangedFields({
    currentValues: currentLocationValues,
    initialValues: initialLocationValues,
  })

  return {
    [ELocationKeys.id]: locationId,
    ...normalizeEmptyStringsToNull(changedFields),
  }
}

export const getCreateLocationPayload = (
  values: TLocationForm,
): TCreateLocationPayload => {
  const currentLocationValues = pick(values, CREATE_LOCATION_OPTIONAL_FIELDS)
  const initialLocationValues = pick(
    getDefaultLocationFormValues(),
    CREATE_LOCATION_OPTIONAL_FIELDS,
  )
  const changedFields = getChangedFields({
    currentValues: currentLocationValues,
    initialValues: initialLocationValues,
  })

  return {
    [ELocationKeys.name]: values[ELocationFormKeys.name],
    [ELocationKeys.type]: values[ELocationFormKeys.type],
    ...normalizeEmptyStringsToNull(changedFields),
  }
}

export const getAgentAssignmentChanges = ({
  initialAgentIds,
  locationId,
  selectedAgentIds,
}: {
  initialAgentIds: string[]
  locationId: string
  selectedAgentIds: string[]
}): TBulkAssignAgentEntry[] => {
  const initialIds = new Set(initialAgentIds)
  const selectedIds = new Set(selectedAgentIds)

  return [
    ...selectedAgentIds
      .filter((agentId) => !initialIds.has(agentId))
      .map((agentId) => ({ agentId, locationId })),
    ...initialAgentIds
      .filter((agentId) => !selectedIds.has(agentId))
      .map((agentId) => ({ agentId, locationId: null })),
  ]
}
